import React from 'react';
import {
  isTrackReference,
  LiveKitRoom,
  LiveKitRoomProps,
  VideoTrack,
  VideoTrackProps,
  useTracks,
} from '@livekit/components-react';
import {Track} from 'livekit-client';
import {LiveSessionSummary} from '../../../../services/liveSessionsApi';
import HomeModalShell from './HomeModalShell';
import LiveSessionIdentity from './LiveSessionIdentity';
import styles from '../Home.module.css';

const LiveKitRoomComponent = LiveKitRoom as React.ComponentType<LiveKitRoomProps>;
const VideoTrackComponent = VideoTrack as React.ForwardRefExoticComponent<
  VideoTrackProps & React.RefAttributes<HTMLVideoElement>
>;

const LIVE_PREVIEW_CAPTURE_DELAY_MS = 1200;
const LIVE_PREVIEW_CAPTURE_RETRY_DELAY_MS = 1000;
const LIVE_PREVIEW_CAPTURE_MAX_ATTEMPTS = 5;
const LIVE_PREVIEW_READY_TIMEOUT_MS = 5000;
const LIVE_PREVIEW_MAX_WIDTH = 960;
const LIVE_PREVIEW_JPEG_QUALITY = 0.82;

type LiveBroadcastPhase =
  | 'idle'
  | 'requesting-media'
  | 'starting-session'
  | 'connecting'
  | 'live'
  | 'ending';

interface LiveBroadcastModalProps {
  isOpen: boolean;
  session: LiveSessionSummary | null;
  accessToken: string | null;
  liveKitUrl: string | null;
  phase: LiveBroadcastPhase;
  error: string | null;
  closeDisabled?: boolean;
  onClose: () => void;
  onEndLive: () => void;
  onConnected: () => void;
  onDisconnected: () => void;
  onError: (error: Error) => void;
  onMediaDeviceFailure: (message: string) => void;
  onPreviewCapture: (previewBlob: Blob) => void | Promise<void>;
}

const getPhaseTitle = (phase: LiveBroadcastPhase) => {
  switch (phase) {
    case 'requesting-media':
      return 'Checking camera and microphone';
    case 'starting-session':
      return 'Creating your live room';
    case 'connecting':
      return 'Connecting to LiveKit';
    case 'live':
      return 'You are live';
    case 'ending':
      return 'Ending your session';
    case 'idle':
    default:
      return 'Go Live';
  }
};

const getPhaseDescription = (phase: LiveBroadcastPhase) => {
  switch (phase) {
    case 'requesting-media':
      return 'Allow camera and microphone access so your live session can start cleanly.';
    case 'starting-session':
      return 'We are creating your room and issuing your secure publisher token.';
    case 'connecting':
      return 'Your camera preview will appear as soon as the broadcast connection is ready.';
    case 'live':
      return 'All signed-in users can discover this session from the Live Now feed.';
    case 'ending':
      return 'Wrapping up the stream and closing the room for viewers.';
    case 'idle':
    default:
      return 'Start a camera-and-microphone live session for your audience.';
  }
};

const formatElapsed = (startedAt?: string) => {
  if (!startedAt) {
    return 'Starting...';
  }

  const startTimestamp = Date.parse(startedAt);

  if (!Number.isFinite(startTimestamp)) {
    return 'Starting...';
  }

  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - startTimestamp) / 1000));
  const hours = Math.floor(elapsedSeconds / 3600);
  const minutes = Math.floor((elapsedSeconds % 3600) / 60);
  const seconds = elapsedSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const waitForPreviewVideoReady = (video: HTMLVideoElement) => new Promise<void>((resolve, reject) => {
  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
    resolve();
    return;
  }

  const timeoutId = window.setTimeout(() => {
    cleanup();
    reject(new Error('Camera preview was not ready to capture.'));
  }, LIVE_PREVIEW_READY_TIMEOUT_MS);

  function cleanup() {
    window.clearTimeout(timeoutId);
    video.removeEventListener('loadeddata', handleReady);
    video.removeEventListener('canplay', handleReady);
    video.removeEventListener('playing', handleReady);
  }

  function handleReady() {
    if (video.videoWidth <= 0 || video.videoHeight <= 0) {
      return;
    }

    cleanup();
    resolve();
  }

  video.addEventListener('loadeddata', handleReady);
  video.addEventListener('canplay', handleReady);
  video.addEventListener('playing', handleReady);
});

const captureLivePreviewBlob = async (video: HTMLVideoElement) => {
  await waitForPreviewVideoReady(video);

  const sourceWidth = video.videoWidth;
  const sourceHeight = video.videoHeight;
  const scale = Math.min(1, LIVE_PREVIEW_MAX_WIDTH / sourceWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sourceWidth * scale));
  canvas.height = Math.max(1, Math.round(sourceHeight * scale));

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Unable to prepare the live preview image.');
  }

  context.drawImage(video, 0, 0, canvas.width, canvas.height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to capture the live preview image.'));
        return;
      }

      resolve(blob);
    }, 'image/jpeg', LIVE_PREVIEW_JPEG_QUALITY);
  });
};

const BroadcasterPreview: React.FC<{
  sessionId?: string;
  onPreviewCapture: (previewBlob: Blob) => void | Promise<void>;
}> = ({sessionId, onPreviewCapture}) => {
  const trackReferences = useTracks([{source: Track.Source.Camera, withPlaceholder: true}]);
  const localCameraTrack = trackReferences.find((trackReference) => trackReference.participant.isLocal);
  const previewVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const capturedSessionIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (
      !sessionId ||
      !localCameraTrack ||
      !isTrackReference(localCameraTrack) ||
      capturedSessionIdsRef.current.has(sessionId)
    ) {
      return;
    }

    capturedSessionIdsRef.current.add(sessionId);

    let isCanceled = false;
    let captureTimeoutId: number | undefined;

    const scheduleCapture = (attempt: number) => {
      captureTimeoutId = window.setTimeout(() => {
        if (isCanceled) {
          return;
        }

        const previewVideo = previewVideoRef.current;

        if (!previewVideo) {
          if (attempt < LIVE_PREVIEW_CAPTURE_MAX_ATTEMPTS) {
            scheduleCapture(attempt + 1);
            return;
          }

          capturedSessionIdsRef.current.delete(sessionId);
          return;
        }

        void captureLivePreviewBlob(previewVideo)
          .then((previewBlob) => {
            if (isCanceled) {
              return;
            }

            void Promise.resolve(onPreviewCapture(previewBlob)).catch((error) => {
              capturedSessionIdsRef.current.delete(sessionId);
              console.error('Failed to save live preview image:', error);
            });
          })
          .catch((error) => {
            if (isCanceled) {
              return;
            }

            if (attempt < LIVE_PREVIEW_CAPTURE_MAX_ATTEMPTS) {
              scheduleCapture(attempt + 1);
              return;
            }

            capturedSessionIdsRef.current.delete(sessionId);
            console.error('Failed to capture live preview image:', error);
          });
      }, attempt === 1 ? LIVE_PREVIEW_CAPTURE_DELAY_MS : LIVE_PREVIEW_CAPTURE_RETRY_DELAY_MS);
    };

    scheduleCapture(1);

    return () => {
      isCanceled = true;
      if (captureTimeoutId) {
        window.clearTimeout(captureTimeoutId);
      }
    };
  }, [localCameraTrack, onPreviewCapture, sessionId]);

  return (
    <div className={styles.liveModalVideoShell}>
      {localCameraTrack && isTrackReference(localCameraTrack) ? (
        <VideoTrackComponent
          ref={previewVideoRef}
          className={styles.liveModalVideo}
          trackRef={localCameraTrack}
        />
      ) : (
        <div className={styles.liveModalVideoPlaceholder}>
          Camera preview will appear here once the broadcast connects.
        </div>
      )}
    </div>
  );
};

const LiveTimer: React.FC<{startedAt?: string}> = ({startedAt}) => {
  const [, forceRefresh] = React.useState(0);

  React.useEffect(() => {
    if (!startedAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      forceRefresh((currentValue) => currentValue + 1);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [startedAt]);

  return <span>{formatElapsed(startedAt)}</span>;
};

const LiveBroadcastModal: React.FC<LiveBroadcastModalProps> = ({
  isOpen,
  session,
  accessToken,
  liveKitUrl,
  phase,
  error,
  closeDisabled = false,
  onClose,
  onEndLive,
  onConnected,
  onDisconnected,
  onError,
  onMediaDeviceFailure,
  onPreviewCapture,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <HomeModalShell
      backdropClassName={styles.modalBackdrop}
      panelClassName={`${styles.modalCard} ${styles.liveModalCard}`.trim()}
      ariaLabelledBy="live-broadcast-modal-title"
      ariaDescribedBy="live-broadcast-modal-description"
      onClose={onClose}
      closeDisabled={closeDisabled}
    >
      <div className={styles.modalHeader}>
        <div>
          <h2 id="live-broadcast-modal-title" className={styles.modalTitle}>
            {getPhaseTitle(phase)}
          </h2>
          <p id="live-broadcast-modal-description" className={styles.modalDescription}>
            {getPhaseDescription(phase)}
          </p>
        </div>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          disabled={closeDisabled}
          aria-label="Close live broadcast modal"
        >
          ×
        </button>
      </div>

      <div className={styles.liveModalMetaRow}>
        <div className={styles.liveModalPill}>
          {phase === 'live' ? 'Broadcasting' : phase === 'ending' ? 'Ending' : 'Preparing'}
        </div>
        <div className={`${styles.liveModalPill} ${styles.liveModalPillAccent}`}>
          <LiveTimer startedAt={session?.startedAt || session?.createdAt} />
        </div>
      </div>

      {session ? (
        <div className={styles.liveModalSessionSummary}>
          <LiveSessionIdentity broadcaster={session.broadcaster} label={session.title} />
          <span className={styles.liveModalSessionMeta}>
            {session.viewerCount} watching
          </span>
        </div>
      ) : null}

      {accessToken && liveKitUrl ? (
        <LiveKitRoomComponent
          token={accessToken}
          serverUrl={liveKitUrl}
          connect={phase !== 'ending'}
          audio={true}
          video={true}
          onConnected={onConnected}
          onDisconnected={() => {
            onDisconnected();
          }}
          onError={onError}
          onMediaDeviceFailure={(_failure, kind) => {
            const kindLabel = kind === 'audioinput'
              ? 'microphone'
              : kind === 'videoinput'
                ? 'camera'
                : 'media device';

            onMediaDeviceFailure(`Your ${kindLabel} could not be started for this live session.`);
          }}
        >
          <BroadcasterPreview
            sessionId={session?.id}
            onPreviewCapture={onPreviewCapture}
          />
        </LiveKitRoomComponent>
      ) : (
        <div className={styles.liveModalVideoShell}>
          <div className={styles.liveModalVideoPlaceholder}>
            {phase === 'requesting-media'
              ? 'Waiting for camera and microphone permission...'
              : 'Preparing your live room...'}
          </div>
        </div>
      )}

      {error ? <p className={styles.liveModalError}>{error}</p> : null}

      <div className={styles.liveModalActions}>
        <button
          type="button"
          className={styles.liveModalEndButton}
          onClick={onEndLive}
          disabled={phase !== 'live'}
        >
          End Live
        </button>
        <span className={styles.liveModalHint}>
          Keep this modal open while you are broadcasting.
        </span>
      </div>
    </HomeModalShell>
  );
};

export default React.memo(LiveBroadcastModal);
